import { DataProvider, HttpError } from 'react-admin';

import { requirePartner } from '@src/providers/dataProvider/shared';

import { fetchSettings } from './fetchSettings';
import type { PartnerSettings } from './types';

/**
 * Internal interface with proper types for settings data provider.
 * React-admin's DataProvider is generic which prevents type-safe single-resource providers.
 */
interface SettingsDataProviderImpl {
  getOne: () => Promise<{ data: PartnerSettings & { id: number } }>;
  getList: () => Promise<never>;
  getMany: () => Promise<never>;
  getManyReference: () => Promise<never>;
  create: () => Promise<never>;
  update: () => Promise<never>;
  updateMany: () => Promise<never>;
  delete: () => Promise<never>;
  deleteMany: () => Promise<never>;
}

/**
 * Data provider for settings resource.
 * Read-only, fetches partner settings.
 */
const settingsDataProviderImpl: SettingsDataProviderImpl = {
  getOne: async () => {
    try {
      const partner = requirePartner();

      const settings = await fetchSettings({
        partnerId: partner.id,
      });

      return {
        data: { id: partner.id, ...settings },
      };
    } catch (error) {
      console.error('settingsDataProvider.getOne failed:', {
        error: error instanceof Error ? error.message : error,
      });
      const status = (error as { status?: number }).status || 500;
      throw new HttpError('Failed to load settings.', status);
    }
  },

  // Not implemented - settings is read-only
  getList: async () => { throw new Error('getList not implemented for settings'); },
  getMany: async () => { throw new Error('getMany not implemented for settings'); },
  getManyReference: async () => { throw new Error('getManyReference not implemented for settings'); },
  create: async () => { throw new Error('create not implemented for settings'); },
  update: async () => { throw new Error('update not implemented for settings'); },
  updateMany: async () => { throw new Error('updateMany not implemented for settings'); },
  delete: async () => { throw new Error('delete not implemented for settings'); },
  deleteMany: async () => { throw new Error('deleteMany not implemented for settings'); },
};

// Cast to DataProvider for react-admin compatibility.
// React-admin's DataProvider methods are generic, which prevents type-safe
// single-resource providers. We define proper types above and cast here.
export const settingsDataProvider = settingsDataProviderImpl as unknown as DataProvider;
